/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  IconButton,
  Modal,
  ModalFooter,
} from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { useBarcodeSession } from '../../../../../../../contexts/BarcodeSessionContext';

const useUndoStack = ({
  fields,
  setSelectedItems,
  setSelectAll,
  resetVolumeStates,
}) => {
  // ✅ Undo functionality state
  const [undoStack, setUndoStack] = useState([]);
  const { restoreBarcodes, removeBarcodes } = useBarcodeSession();
  const [showUndoNotification, setShowUndoNotification] = useState(false);
  const [undoTimer, setUndoTimer] = useState(null);
  const [showUndoHistory, setShowUndoHistory] = useState(false);

  // ✅ Create undo action
  const createUndoAction = (type, data, description) => {
    const undoAction = {
      id: uuidv4(),
      type,
      data,
      description,
      timestamp: Date.now(),
    };

    setUndoStack((prev) => [...prev.slice(-9), undoAction]); // Keep last 10 actions
    setShowUndoNotification(true);

    // Auto-hide notification after 10 seconds
    if (undoTimer) {
      clearTimeout(undoTimer);
    }

    const newTimer = setTimeout(() => {
      setShowUndoNotification(false);
    }, 10000);

    setUndoTimer(newTimer);
  };

  // ✅ Execute undo action
  const executeUndo = (undoAction) => {
    if (!undoAction || !undoAction.data) {
      console.warn('Invalid undo action');
      return;
    }

    switch (undoAction.type) {
      case 'DELETE_ITEMS':
        // ✅ Check if deletedItems exists and is an array
        if (
          undoAction.data.deletedItems &&
          Array.isArray(undoAction.data.deletedItems)
        ) {
          // Restore deleted items
          undoAction.data.deletedItems.forEach(({ item, index }) => {
            if (item && typeof index === 'number') {
              // Insert item at its original position
              if (index <= fields.length) {
                fields.insert(index, item);
              } else {
                fields.push(item);
              }
            }
          });

          // Restore barcodes to session tracking
          if (undoAction.data.deletedBarcodes && undoAction.data.deletedBarcodes.length > 0) {
            restoreBarcodes(undoAction.data.deletedBarcodes);
          }

          // Items restored successfully
        }
        break;

      case 'DELETE_VOLUME':
        // ✅ Check if deletedItems exists and is an array
        if (
          undoAction.data.deletedItems &&
          Array.isArray(undoAction.data.deletedItems)
        ) {
          // Restore entire volume
          undoAction.data.deletedItems.forEach(({ item, index }) => {
            if (item && typeof index === 'number') {
              if (index <= fields.length) {
                fields.insert(index, item);
              } else {
                fields.push(item);
              }
            }
          });

          // Restore barcodes to session tracking
          if (undoAction.data.deletedBarcodes && undoAction.data.deletedBarcodes.length > 0) {
            restoreBarcodes(undoAction.data.deletedBarcodes);
          }

          // Volume restored successfully
        }
        break;

      case 'DELETE_ALL_VOLUMES':
        // ✅ Check if allItems exists and is an array
        if (
          undoAction.data.allItems &&
          Array.isArray(undoAction.data.allItems)
        ) {
          // Restore all items
          undoAction.data.allItems.forEach((item) => {
            if (item) {
              fields.push(item);
            }
          });

          // Restore barcodes to session tracking
          if (undoAction.data.deletedBarcodes && undoAction.data.deletedBarcodes.length > 0) {
            restoreBarcodes(undoAction.data.deletedBarcodes);
          }

          // All volumes restored successfully
        }
        break;

      case 'RESET_VOLUME':
        // ✅ Check if originalItems exists and is an array
        if (
          undoAction.data.originalItems &&
          Array.isArray(undoAction.data.originalItems)
        ) {
          // Restore original volume data
          undoAction.data.originalItems.forEach(({ item, index }) => {
            if (item && typeof index === 'number' && index < fields.length) {
              fields.update(index, item);
            }
          });
        }
        break;

      default:
        console.warn('Unknown undo action type:', undoAction.type);
        return;
    }

    // Remove the executed undo action from stack
    setUndoStack((prev) =>
      prev.filter((action) => action.id !== undoAction.id),
    );
    setShowUndoNotification(false);

    // Reset selections
    setSelectedItems([]);
    setSelectAll(false);
    resetVolumeStates();
  };

  // ✅ Clear undo stack with barcode cleanup
  const clearUndoStack = () => {
    // Extract all barcodes from pending undo actions before clearing
    const allPendingBarcodes = undoStack
      .map(action => action.data.deletedBarcodes || [])
      .flat()
      .filter(Boolean);

    // Remove any remaining "orphaned" barcodes that were marked for undo but won't be restored
    if (allPendingBarcodes.length > 0) {
      // These barcodes were from deleted items that the user chose not to restore
      // They should remain removed from session tracking
      // No action needed as they were already removed during deletion
    }

    setUndoStack([]);
    setShowUndoNotification(false);
    if (undoTimer) {
      clearTimeout(undoTimer);
    }
  };

  // Track undo stack changes
  useEffect(() => {
    // console.log('Undo stack changed. New length:', undoStack.length);
  }, [undoStack]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimer) {
        clearTimeout(undoTimer);
      }
    };
  }, [undoTimer]);

  // ✅ Undo notification component
  const renderUndoNotification = () => {
    if (!showUndoNotification || undoStack.length === 0) return null;

    const latestAction = undoStack[undoStack.length - 1];

    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2d3748',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '300px',
          maxWidth: '500px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.undoNotification.success"
              defaultMessage="Operation completed successfully"
            />
          </div>
          <div style={{ fontSize: '12px', color: '#cbd5e0' }}>
            <FormattedMessage
              id={`ui-plugin-inventory-templates.${latestAction.description}`}
              values={{
                volume: latestAction.data?.volume || '',
                newVolume: latestAction.data?.newVolumeString || '',
                count: latestAction.data?.deletedItems?.length || latestAction.data?.allItems?.length || latestAction.data?.originalItems?.length || 0,
              }}
              defaultMessage={
                latestAction.description === 'undoSystem.actions.deleteItem' ? 'Delete item' :
                  latestAction.description === 'undoSystem.actions.deleteItems' ? 'Delete {count} item(s)' :
                    latestAction.description === 'undoSystem.actions.deleteVolume' ? 'Delete volume "{volume}" ({count} items)' :
                      latestAction.description === 'undoSystem.actions.deleteAllVolumes' ? 'Delete all volumes ({count} items)' :
                        latestAction.description === 'undoSystem.actions.resetVolume' ? 'Reset volume "{volume}" to "{newVolume}"' :
                          latestAction.description
              }
            />
          </div>
        </div>

        <Button
          buttonStyle="primary"
          onClick={() => executeUndo(latestAction)}
          marginBottom0
          style={{
            backgroundColor: '#4299e1',
            border: 'none',
            padding: '6px 12px',
            fontSize: '12px',
          }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.undoNotification.undoButton"
            defaultMessage="Undo"
          />
        </Button>

        <IconButton
          icon="times"
          onClick={() => setShowUndoNotification(false)}
          style={{
            color: '#a0aec0',
            backgroundColor: 'transparent',
            border: 'none',
            padding: '4px',
          }}
        />
      </div>
    );
  };

  // ✅ Undo history panel (optional - can be toggled)
  const renderUndoHistory = () => {
    if (!showUndoHistory) return null;

    return (
      <Modal
        open={showUndoHistory}
        onClose={() => setShowUndoHistory(false)}
        label={
          <FormattedMessage
            id="ui-plugin-inventory-templates.undoHistory.modal.title"
            defaultMessage="Operations History"
          />
        }
        size="medium"
      >
        <div style={{ padding: '20px' }}>
          <h4 style={{ marginBottom: '8px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.undoHistory.modal.header"
              defaultMessage="Recent Operations History"
            />
          </h4>

          {undoStack.length > 0 && (
            <p
              style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '16px',
                backgroundColor: '#f0f9ff',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #bfdbfe',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.undoHistory.modal.instructions"
                defaultMessage="You can undo multiple operations consecutively - the window will remain open until you close it manually"
              />
            </p>
          )}

          {undoStack.length === 0 ? (
            <div>
              <div
                style={{
                  textAlign: 'center',
                  color: '#6c757d',
                  padding: '40px',
                }}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.undoHistory.modal.empty"
                  defaultMessage="No operations in history"
                />
              </div>
              <div
                style={{
                  textAlign: 'center',
                  color: '#16a34a',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: '#f0fdf4',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  margin: '0 20px',
                  border: '1px solid #bbf7d0',
                }}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.undoHistory.modal.emptyAction"
                  defaultMessage="You can close this window and continue"
                />
              </div>
            </div>
          ) : (
            <div>
              {undoStack
                .slice()
                .reverse()
                .map((action, index) => (
                  <div
                    key={action.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: index === 0 ? '#e8f5e8' : '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      marginBottom: '8px',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>
                        <FormattedMessage
                          id={`ui-plugin-inventory-templates.${action.description}`}
                          values={{
                            volume: action.data?.volume || '',
                            newVolume: action.data?.newVolumeString || '',
                            count: action.data?.deletedItems?.length || action.data?.allItems?.length || action.data?.originalItems?.length || 0,
                          }}
                          defaultMessage={
                            action.description === 'undoSystem.actions.deleteItem' ? 'Delete item' :
                              action.description === 'undoSystem.actions.deleteItems' ? 'Delete {count} item(s)' :
                                action.description === 'undoSystem.actions.deleteVolume' ? 'Delete volume "{volume}" ({count} items)' :
                                  action.description === 'undoSystem.actions.deleteAllVolumes' ? 'Delete all volumes ({count} items)' :
                                    action.description === 'undoSystem.actions.resetVolume' ? 'Reset volume "{volume}" to "{newVolume}"' :
                                      action.description
                          }
                        />
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                        {new Date(action.timestamp).toLocaleTimeString('ar-SA')}
                      </div>
                    </div>

                    <Button
                      buttonStyle={index === 0 ? 'primary' : 'default'}
                      onClick={() => {
                        executeUndo(action);
                        // ✅ Modal stays open for multiple undos
                      }}
                      marginBottom0
                      disabled={index !== 0} // Only allow undoing the latest action
                    >
                      {index === 0 ? (
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.undoHistory.modal.undoButton"
                          defaultMessage="Undo"
                        />
                      ) : (
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.undoHistory.modal.doneButton"
                          defaultMessage="Done"
                        />
                      )}
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            onClick={() => setShowUndoHistory(false)}
            marginBottom0
            buttonStyle="primary"
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.undoHistory.modal.closeButton"
              defaultMessage="Close"
            />
          </Button>

          {undoStack.length > 0 && (
            <Button
              buttonStyle="danger"
              onClick={() => {
                clearUndoStack();
                setShowUndoHistory(false);
              }}
              marginBottom0
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.undoHistory.modal.clearAllButton"
                defaultMessage="Clear All & Close"
              />
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  };

  return {
    // State
    undoStack,
    showUndoHistory,

    // Actions
    createUndoAction,
    executeUndo,
    clearUndoStack,
    setShowUndoHistory,

    // Components
    renderUndoNotification,
    renderUndoHistory,
  };
};

export default useUndoStack;
